// Fatia S3b: sugestões com filtros, detalhes do que muda, aplicar/desfazer e "agora não".
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { brl, num } from '@impulso/shared'
import { useCan, useCompany } from '../../lib/company'
import { ErrorState, Loading, PageHead, Tabs } from '../../ui'
import { useRecommendations } from '../../features/recommendations/api'
import SuggestionCard from '../../features/recommendations/SuggestionCard'

type Filter = 'all' | 'growth' | 'savings' | 'fix'

export default function Suggestions() {
  const company = useCompany()
  const canApply = useCan('company.apply_suggestion')
  const [filter, setFilter] = useState<Filter>('all')
  const q = useRecommendations(company.id, 'open')
  const items = q.data?.items ?? []
  const visible = items.filter((s) => filter === 'all' || s.kind === filter)
  const count = (k: Filter) => items.filter((s) => s.kind === k).length
  const sum = q.data?.summary
  const reason = q.data?.emptyReason

  return (
    <>
      <PageHead title="Sugestões" sub="Analisamos suas contas todos os dias. Cada sugestão explica o porquê e mostra exatamente o que muda antes de você aprovar." />

      {q.isLoading && <Loading lines={5} />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

      {q.data && (
        <>
          {sum && sum.open > 0 && (sum.growthResults > 0 || sum.savings > 0) && (
            <div className="callout" style={{ marginBottom: 20 }}>
              <Sparkles size={20} color="var(--primary)" style={{ flexShrink: 0 }} />
              <div>
                <div className="strong" style={{ color: 'var(--text)' }}>
                  Se você aplicar todas: {[
                    sum.growthResults > 0 ? `cerca de +${num(Math.round(sum.growthResults))} clientes` : null,
                    sum.savings > 0 ? `${brl(sum.savings)} de economia` : null,
                  ].filter(Boolean).join(' e ')} por mês.
                </div>
                <div className="small">Nenhuma mudança aumenta seu orçamento mensal sem você pedir.</div>
              </div>
            </div>
          )}

          <div className="row wrap" style={{ marginBottom: 16, overflowX: 'auto' }}>
            <Tabs<Filter>
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: `Todas (${items.length})` },
                { value: 'growth', label: `Mais clientes (${count('growth')})` },
                { value: 'savings', label: `Economia (${count('savings')})` },
                { value: 'fix', label: `Correções (${count('fix')})` },
              ]}
            />
          </div>

          <div className="stack">
            {visible.map((s) => <SuggestionCard key={s.id} s={s} companyId={company.id} canApply={canApply} />)}
            {visible.length === 0 && (
              <div className="card empty">
                {items.length === 0 && reason === 'no_connections' ? (
                  <p>Você ainda não conectou nenhuma conta de anúncios, então não há o que analisar. <Link className="strong" to={`/app/${company.id}/contas`}>Conectar uma conta</Link></p>
                ) : items.length === 0 && reason === 'syncing' ? (
                  <p>Ainda estamos sincronizando os dados das suas contas. As primeiras sugestões aparecem assim que tivermos alguns dias de resultados. <Link className="strong" to={`/app/${company.id}/contas`}>Ver contas conectadas</Link></p>
                ) : (
                  <p>Nada por aqui. Avisamos assim que surgir uma oportunidade.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
